import React from 'react';
import { FiMail, FiMessageCircle } from 'react-icons/fi';
import styles from './NotificationSettingsTable.module.css';

/**
 * Reusable per-event Email/WhatsApp toggle table. Renders a plain global <table>
 * (app table styling); the page wraps it in `.card` > `.table-container`.
 * Props:
 *  - events: [{ key, label, description, critical, supportsEmail, supportsWhatsapp,
 *               whatsappTemplateReady, emailEnabled, whatsappEnabled, reminderCadence? }]
 *  - onToggle(eventKey, channel, nextValue)
 *  - onCadenceChange(eventKey, value)   (optional; only used when a row has reminderCadence)
 *  - channelDisabled: { email: bool, whatsapp: bool }  (upstream tier disabled -> grey out + hint)
 *  - showCadence: bool
 */
const Toggle = ({ checked, disabled, onChange, title }) => (
  <label className={styles.switch} title={title}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    <span className={styles.slider} />
  </label>
);

const NotificationSettingsTable = ({
  events = [], onToggle, onCadenceChange, channelDisabled = {}, showCadence = false,
}) => (
  <table>
    <thead>
      <tr>
        <th>Notification</th>
        <th className={styles.center}><FiMail /> Email</th>
        <th className={styles.center}><FiMessageCircle /> WhatsApp</th>
        {showCadence && <th className={styles.center}>Reminder</th>}
      </tr>
    </thead>
    <tbody>
      {events.map((e) => {
        const emailDisabled = !e.supportsEmail || channelDisabled.email;
        const waDisabled = !e.supportsWhatsapp || channelDisabled.whatsapp || !e.whatsappTemplateReady;
        return (
          <tr key={e.key}>
            <td className={styles.eventCell}>
              <div className={styles.eventLabel}>
                {e.label}
                {e.critical && <span className={styles.badge}>Always on</span>}
              </div>
              <div className={styles.eventDesc}>{e.description}</div>
            </td>
            <td className={styles.center}>
              {e.critical ? <span className={styles.muted}>—</span> : (
                <Toggle
                  checked={e.emailEnabled}
                  disabled={emailDisabled}
                  onChange={(v) => onToggle(e.key, 'email', v)}
                  title={channelDisabled.email ? 'Email is turned off for this derasar by the platform admin' : ''}
                />
              )}
            </td>
            <td className={styles.center}>
              {e.critical ? <span className={styles.muted}>—</span> : (
                <Toggle
                  checked={e.whatsappEnabled}
                  disabled={waDisabled}
                  onChange={(v) => onToggle(e.key, 'whatsapp', v)}
                  title={!e.whatsappTemplateReady ? 'WhatsApp template coming soon' : (channelDisabled.whatsapp ? 'WhatsApp is turned off for this derasar by the platform admin' : '')}
                />
              )}
            </td>
            {showCadence && (
              <td className={styles.center}>
                {e.reminderCadence !== null && e.reminderCadence !== undefined ? (
                  <select
                    className={styles.select}
                    value={e.reminderCadence}
                    onChange={(ev) => onCadenceChange(e.key, ev.target.value)}
                  >
                    <option value="off">Off</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                ) : <span className={styles.muted}>—</span>}
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  </table>
);

export default NotificationSettingsTable;
